<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import { AI_AGENT_METRICS } from 'dashboard/store/modules/reports';
import { GROUP_BY_FILTER } from '../constants';

const props = defineProps({
  groupBy: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const accountReport = useMapGetter('getAccountReports');

const formatLabel = timestamp => {
  const date = fromUnixTime(timestamp);
  if (props.groupBy?.period === GROUP_BY_FILTER[3].period) {
    return format(date, 'MMM-yyyy');
  }
  if (props.groupBy?.period === GROUP_BY_FILTER[4].period) {
    return format(date, 'yyyy');
  }
  return format(date, 'dd-MMM');
};

// Períodos (colunas) derivados da 1ª métrica com dados — todas compartilham os mesmos buckets.
const periods = computed(() => {
  const series = AI_AGENT_METRICS.map(
    key => accountReport.value.data[key]
  ).find(data => data && data.length);
  return (series || []).map(point => point.timestamp);
});

const rows = computed(() =>
  AI_AGENT_METRICS.map(key => {
    const data = accountReport.value.data[key] || [];
    const byTimestamp = Object.fromEntries(
      data.map(point => [point.timestamp, point.value])
    );
    const values = periods.value.map(ts => byTimestamp[ts] || 0);
    return {
      key,
      label: t(`REPORT.METRICS.${key.toUpperCase()}.NAME`),
      values,
      total: values.reduce((sum, value) => sum + value, 0),
    };
  })
);
</script>

<template>
  <div
    class="px-6 py-5 mt-4 overflow-x-auto shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <h3 class="mb-4 text-base font-medium text-n-slate-12">
      {{ $t('AI_AGENT_REPORTS.TABLE.TITLE') }}
    </h3>
    <table class="min-w-full text-sm border-collapse">
      <thead>
        <tr class="text-left border-b border-n-container">
          <th class="py-2 pr-4 font-medium text-n-slate-11">
            {{ $t('AI_AGENT_REPORTS.TABLE.METRIC') }}
          </th>
          <th
            v-for="ts in periods"
            :key="ts"
            class="px-3 py-2 font-medium text-right text-n-slate-11 whitespace-nowrap"
          >
            {{ formatLabel(ts) }}
          </th>
          <th class="px-3 py-2 font-semibold text-right text-n-slate-12">
            {{ $t('AI_AGENT_REPORTS.TABLE.TOTAL') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in rows"
          :key="row.key"
          class="border-b border-n-container/60"
        >
          <td class="py-2 pr-4 text-n-slate-12 whitespace-nowrap">
            {{ row.label }}
          </td>
          <td
            v-for="(value, index) in row.values"
            :key="index"
            class="px-3 py-2 text-right tabular-nums text-n-slate-11"
          >
            {{ value.toLocaleString() }}
          </td>
          <td class="px-3 py-2 font-semibold text-right tabular-nums text-n-slate-12">
            {{ row.total.toLocaleString() }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
